// src/pages/KYCUploadPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import './KycUpload.css';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import QRCode from 'qrcode';
import * as signalR from '@microsoft/signalr';

const MIN_CONFIDENCE = 0.65;

// Backend routes (axiosInstance.baseURL should already include /api)
const CAPTURE_API = {
  CREATE_SESSION: '/kyc/capture-sessions',
  GET_SESSION: (id) => `/kyc/capture-sessions/${id}`,
  GET_FILE: (id, kind) => `/kyc/capture-sessions/${id}/files/${kind}`,
};

const STORAGE_KEYS = {
  PendingImport: 'kyc_capture_pending_import', // JSON: { sessionId }
};

function safeIsoToLocal(iso) {
  try {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso || '';
  }
}

function isExpired(iso) {
  try {
    if (!iso) return false;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    return Date.now() > d.getTime();
  } catch {
    return false;
  }
}

// Heuristic: treat these server/client messages as “trouble”
function isTroubleMessage(msg) {
  if (!msg || typeof msg !== 'string') return false;
  const m = msg.toLowerCase();

  return (
    m.includes('valid id') ||
    m.includes('clear photo') ||
    m.includes('clearer') ||
    m.includes('unclear') ||
    m.includes('confidence') ||
    m.includes('strongly match') ||
    m.includes('identity document') ||
    m.includes('upload a clear') ||
    m.includes('does not appear') ||
    m.includes('does not strongly match') ||
    m.includes('ocr')
  );
}

/** ---------------- SignalR hub URL helpers ----------------
 * Critical: avoid localhost:3000. Compute hub origin from axiosInstance.defaults.baseURL.
 * Example:
 *   axios baseURL: https://lanesands.io/api  -> hub: https://lanesands.io/hubs/kyc-capture
 */
const HUB_PATH_CANDIDATES = ['/hubs/kyc-capture'];

const computeHubUrlCandidates = () => {
  const apiBase = (axiosInstance.defaults.baseURL || '').replace(/\/+$/, '');
  //console.log('API Base URL:', apiBase);
  const origin = apiBase ? apiBase.replace(/\/api($|\/)/, '') : '';
  //console.log('Computed Origin:', origin);
  const urls = HUB_PATH_CANDIDATES.map((p) => (origin ? `${origin}${p}` : p));
  //console.log('Hub URL candidates:', urls);
  return urls;
};

// Hard reload (reliable)
const hardReload = () => window.location.reload();

const KYCUploadPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  const [identityDocument, setIdentityDocument] = useState(null);
  const [addressProof, setAddressProof] = useState(null);

  const [message, setMessage] = useState('');
  const [apiWarnings, setApiWarnings] = useState([]);
  const [apiConfidence, setApiConfidence] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [kycStatus, setKycStatus] = useState(null);
  const [adminEmails, setAdminEmails] = useState(['support@hms.com']);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  // Phone capture session state
  const [phoneCaptureEnabled, setPhoneCaptureEnabled] = useState(false);
  const [captureSession, setCaptureSession] = useState(null); // { sessionId, captureUrl, expiresAtUtc }
  const [captureQrDataUrl, setCaptureQrDataUrl] = useState(null);
  const [captureStatus, setCaptureStatus] = useState(null); // { hasIdentity, hasAddress, status, expiresAtUtc? }
  const [captureError, setCaptureError] = useState('');
  const [isCreatingCapture, setIsCreatingCapture] = useState(false);

  const pollRef = useRef(null);
  const createInFlightRef = useRef(false);

  // SignalR refs
  const hubConnRef = useRef(null);
  const hubStartedRef = useRef(false);
  const hubSidJoinedRef = useRef(null);
  const toastOnceRef = useRef(false);

  // prevents multiple reload prompts
  const reloadTriggeredRef = useRef(false);

  const token = authData?.token || null;

  // “Trouble” = low confidence OR warnings OR a doc-quality error message
  const isHavingTrouble = useMemo(() => {
    const lowConfidence = typeof apiConfidence === 'number' && apiConfidence < MIN_CONFIDENCE;
    const hasWarnings = Array.isArray(apiWarnings) && apiWarnings.length > 0;
    const msgTrouble = isTroubleMessage(message);
    return lowConfidence || hasWarnings || msgTrouble;
  }, [apiConfidence, apiWarnings, message]);

  // Show the right column only if the user is having trouble (or already enabled phone capture)
  const showTroubleColumn = useMemo(() => {
    return phoneCaptureEnabled || isHavingTrouble;
  }, [phoneCaptureEnabled, isHavingTrouble]);

  const canSubmit = useMemo(() => {
    const hasFiles = !!identityDocument && !!addressProof;
    const confidenceOk = apiConfidence === null || apiConfidence >= MIN_CONFIDENCE;
    return !isLoading && hasFiles && confidenceOk;
  }, [isLoading, identityDocument, addressProof, apiConfidence]);

  // Auto-suggest enabling phone capture when confidence is low
  useEffect(() => {
    if (apiConfidence !== null && apiConfidence < MIN_CONFIDENCE) {
      setPhoneCaptureEnabled(true);
    }
  }, [apiConfidence]);

  // -------------------- INITIAL LOAD (KYC STATUS) --------------------
  useEffect(() => {
    const checkKycStatus = async () => {
      if (!token) {
        setIsCheckingStatus(false);
        return;
      }

      try {
        const profileResponse = await axiosInstance.get('/User/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });

        const statusFromProfile = profileResponse.data?.kycStatus;

        if (statusFromProfile) {
          setKycStatus(statusFromProfile);
        } else {
          const kycResponse = await axiosInstance.get('/User/kyc-status', {
            headers: { Authorization: `Bearer ${token}` },
          });
          setKycStatus(kycResponse.data?.status || null);
        }

        // Fetch admin emails
        try {
          const adminsResponse = await axiosInstance.get('/User/system-admins', {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (Array.isArray(adminsResponse.data) && adminsResponse.data.length > 0) {
            const emailsFromApi = adminsResponse.data
              .map((admin) => admin?.email)
              .filter((email) => email && typeof email === 'string' && email.includes('@'));

            const combinedEmails = Array.from(new Set([...(emailsFromApi || []), 'support@hms.com']));
            if (combinedEmails.length > 0) setAdminEmails(combinedEmails);
          }
        } catch (emailError) {
          //console.log('Using default admin emails', emailError);
        }
      } catch (error) {
        console.error('Error fetching KYC status:', error);
        toast.error('Could not verify your KYC status. Please try again.');
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkKycStatus();
  }, [token]);

  // Cleanup polling & hub on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;

      if (hubConnRef.current) {
        hubConnRef.current.stop().catch(() => {});
      }
      hubConnRef.current = null;
      hubStartedRef.current = false;
      hubSidJoinedRef.current = null;
    };
  }, []);

  const resetApiFeedback = () => {
    setMessage('');
    setApiWarnings([]);
    setApiConfidence(null);
  };

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const maxSize = 100 * 1024 * 1024; // 100MB

    if (!file) return 'Please select a file';

    const ext = (file.name || '').toLowerCase();
    const typeOk =
      allowedTypes.includes(file.type) ||
      ext.endsWith('.jpg') ||
      ext.endsWith('.jpeg') ||
      ext.endsWith('.png') ||
      ext.endsWith('.pdf');

    if (!typeOk) {
      return `Unsupported file type (${file.type || 'unknown'}). Allowed types are JPG, PNG, and PDF.`;
    }

    if (file.size > maxSize) {
      return `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds 100MB limit.`;
    }

    return null;
  };

  // -------------------- PHONE CAPTURE FLOW --------------------
  const stopCapturePolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const stopRealtime = () => {
    if (hubConnRef.current) {
      hubConnRef.current.stop().catch(() => {});
    }
    hubConnRef.current = null;
    hubStartedRef.current = false;
    hubSidJoinedRef.current = null;
  };

  const clearCaptureSession = () => {
    stopCapturePolling();
    stopRealtime();

    setCaptureSession(null);
    setCaptureQrDataUrl(null);
    setCaptureStatus(null);
    setCaptureError('');
    setIsCreatingCapture(false);
    createInFlightRef.current = false;
    toastOnceRef.current = false;
    reloadTriggeredRef.current = false;
  };

  const importCapturedFiles = useCallback(
    async (sessionId) => {
      if (!token) return;

      // cache-buster to avoid stale responses
      const t = Date.now();

      const [idBlobRes, addrBlobRes] = await Promise.all([
        axiosInstance.get(CAPTURE_API.GET_FILE(sessionId, 'identity'), {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          params: { t },
        }),
        axiosInstance.get(CAPTURE_API.GET_FILE(sessionId, 'address'), {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          params: { t },
        }),
      ]);

      const idBlob = idBlobRes.data;
      const addrBlob = addrBlobRes.data;

      const idType = idBlob.type || 'image/jpeg';
      const addrType = addrBlob.type || 'image/jpeg';

      const pickExt = (type) => {
        const tt = (type || '').toLowerCase();
        if (tt.includes('pdf')) return 'pdf';
        if (tt.includes('png')) return 'png';
        return 'jpg';
      };

      const idFile = new File([idBlob], `IdentityDocument_${sessionId}.${pickExt(idType)}`, {
        type: idType,
        lastModified: Date.now(),
      });

      const addrFile = new File([addrBlob], `AddressProof_${sessionId}.${pickExt(addrType)}`, {
        type: addrType,
        lastModified: Date.now(),
      });

      setIdentityDocument(idFile);
      setAddressProof(addrFile);
      resetApiFeedback();
    },
    [token]
  );

  // ✅ After reload, auto-import from server using sessionStorage
  useEffect(() => {
    if (!token) return;

    const raw = sessionStorage.getItem(STORAGE_KEYS.PendingImport);
    if (!raw) return;

    sessionStorage.removeItem(STORAGE_KEYS.PendingImport);

    let sid = '';
    try {
      const obj = JSON.parse(raw);
      sid = obj?.sessionId ? String(obj.sessionId) : '';
    } catch {
      sid = '';
    }

    if (!sid) return;

    (async () => {
      try {
        await importCapturedFiles(sid);
        toast.success('Phone upload imported successfully.');
        setPhoneCaptureEnabled(false);
        clearCaptureSession();
      } catch (e) {
        console.error(e);
        toast.error('Phone upload was detected, but import failed. Please click Refresh QR and try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, importCapturedFiles]);

  // Returns true only when both files exist
   const refreshCaptureStatusOnce = useCallback(
  async (sessionId, expiresAtUtc) => {
    if (!token || !sessionId) return false;

    try {
      const t = Date.now();
      const res = await axiosInstance.get(CAPTURE_API.GET_SESSION(sessionId), {
        headers: { Authorization: `Bearer ${token}` },
        params: { t },
      });

      const raw = res.data || {};

      // Normalize all server shapes into one consistent object
      const normalized = {
        status: (raw.status ?? raw.Status ?? 'Active'),
        expiresAtUtc: (raw.expiresAtUtc ?? raw.ExpiresAtUtc ?? expiresAtUtc),
        hasIdentity: !!(raw.hasIdentity ?? raw.HasIdentity),
        hasAddress:  !!(raw.hasAddress  ?? raw.HasAddress),
      };

      setCaptureStatus(normalized);

      const statusText = String(normalized.status || '').toLowerCase();
      const expiredByServer = statusText === 'expired' || statusText === 'lock' || statusText === 'locked';
      const expiredByTime = isExpired(normalized.expiresAtUtc);

      if (expiredByServer || expiredByTime) {
        stopCapturePolling();
        setCaptureError('Phone capture session expired. Click “Refresh QR” to continue.');
        return false;
      }

      return normalized.hasIdentity && normalized.hasAddress;
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) setCaptureError('Session expired. Please log in again.');
      return false;
    }
  },
  [token]
);


  const startCapturePolling = (sessionId, expiresAtUtc) => {
    stopCapturePolling();
    pollRef.current = setInterval(async () => {
      // polling is a fallback; no need to act on boolean here
      await refreshCaptureStatusOnce(sessionId, expiresAtUtc);
    }, 1500);
  };

  const createCaptureSessionInternal = async (isRefresh = false) => {
    if (!token) return;
    if (createInFlightRef.current) return;

    createInFlightRef.current = true;
    setIsCreatingCapture(true);
    setCaptureError('');
    toastOnceRef.current = false;
    reloadTriggeredRef.current = false;

    try {
      const res = await axiosInstance.post(
        CAPTURE_API.CREATE_SESSION,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const session = res.data; // { sessionId, captureUrl, expiresAtUtc }
      if (!session?.sessionId || !session?.captureUrl) {
        throw new Error('Capture session response invalid.');
      }

      setCaptureSession(session);
      setCaptureStatus(null);

      const qr = await QRCode.toDataURL(session.captureUrl, { margin: 1, width: 180 });
      setCaptureQrDataUrl(qr);

      // Poll fallback (reliable even if SignalR fails)
      startCapturePolling(session.sessionId, session.expiresAtUtc);

      if (!isRefresh) {
        toast.info('Phone capture enabled. Scan the QR code to capture documents.');
      }
    } catch (e) {
      console.error(e);
      setCaptureError(e?.response?.data?.message || e.message || 'Could not create capture session.');
    } finally {
      setIsCreatingCapture(false);
      createInFlightRef.current = false;
    }
  };

  // Create session only when user enables phone capture (or auto-enabled by low confidence)
  useEffect(() => {
    if (!phoneCaptureEnabled) {
      clearCaptureSession();
      return;
    }

    if (!captureSession || !captureQrDataUrl || isExpired(captureSession?.expiresAtUtc)) {
      createCaptureSessionInternal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCaptureEnabled, token]);

  // -------------------- SIGNALR REALTIME (best-effort) --------------------
  useEffect(() => {
    const sid = captureSession?.sessionId;
    if (!phoneCaptureEnabled || !sid) return;

    let disposed = false;

    const connectHub = async () => {
      try {
        // reuse existing connection
        if (hubConnRef.current && hubStartedRef.current) {
          if (hubSidJoinedRef.current !== String(sid)) {
            try {
              await hubConnRef.current.invoke('JoinSession', String(sid));
            } catch {}
            hubSidJoinedRef.current = String(sid);
          }
          return;
        }

        const urls = computeHubUrlCandidates();
        let lastErr = null;

        for (const url of urls) {
          if (disposed) return;

          const conn = new signalR.HubConnectionBuilder()
            .withUrl(url, {
              withCredentials: true,
              accessTokenFactory: token ? () => token : undefined,
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

          // IMPORTANT: remove handler on cleanup when re-creating connection
           conn.on('CaptureUpdated', async (payload) => {
  const payloadSid = String(payload?.sessionId || '');
  const currentSid = String(captureSession?.sessionId || '');

  //console.log('📩 CaptureUpdated received:', payloadSid);
  //console.log('🔎 currentSid=', currentSid, 'reloadTriggered=', reloadTriggeredRef.current);

  if (!payloadSid) return;

  if (payloadSid !== currentSid) {
    //console.log('🚫 Ignoring: sid mismatch', { payloadSid, currentSid });
    return;
  }

  // sometimes notify arrives before the DB status updates; retry a few times
  let done = false;
  for (let i = 0; i < 5; i++) {
    done = await refreshCaptureStatusOnce(payloadSid, captureSession?.expiresAtUtc);
    //console.log(`✅ status check ${i + 1}: done=${done}`, captureStatus);
    if (done) break;
    await new Promise((r) => setTimeout(r, 300));
  }

  if (!done) {
    //console.log('⏳ Not done yet (hasIdentity/hasAddress still false). Polling will catch it.');
    return;
  }

  if (reloadTriggeredRef.current) {
    //console.log('🚫 Already triggered once; skipping.');
    return;
  }

  reloadTriggeredRef.current = true;

  //console.log('🚀 Triggering import now...');

  // BEST: import immediately (no reload needed)
  try {
    await importCapturedFiles(payloadSid);
    toast.success('Phone upload imported successfully.');
    setPhoneCaptureEnabled(false);
    clearCaptureSession();
  } catch (e) {
    console.error('❌ Import failed, falling back to reload', e);
    sessionStorage.setItem(STORAGE_KEYS.PendingImport, JSON.stringify({ sessionId: payloadSid }));
    hardReload();
  }
});


          try {
            await conn.start();
            try {
              await conn.invoke('JoinSession', String(sid));
            } catch {}

            hubConnRef.current = conn;
            hubStartedRef.current = true;
            hubSidJoinedRef.current = String(sid);
            return;
          } catch (e) {
            lastErr = e;
            try {
              await conn.stop();
            } catch {}
          }
        }

        //console.log('SignalR connect failed:', lastErr);
      } catch (e) {
        //console.log('SignalR error:', e);
      }
    };

    connectHub();

    return () => {
      disposed = true;
      // do not stop connection here; main unmount cleanup handles it
      // (if you want per-session teardown, you can stopRealtime() here)
    };
  }, [phoneCaptureEnabled, captureSession?.sessionId, captureSession?.expiresAtUtc, token, refreshCaptureStatusOnce]);

  // Stop hub when phone capture is disabled
  useEffect(() => {
    if (phoneCaptureEnabled) return;
    stopRealtime();
  }, [phoneCaptureEnabled]);

  // -------------------- SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    resetApiFeedback();

    if (!token) {
      toast.error('Your session has expired. Please log in again.');
      return;
    }

    const identityError = validateFile(identityDocument);
    const addressError = validateFile(addressProof);

    if (identityError || addressError) {
      const errorMsg = identityError || addressError;
      setMessage(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('IdentityDocument', identityDocument);
      formData.append('AddressProof', addressProof);

      if (authData?.email) formData.append('Email', authData.email);
      if (authData?.userId) formData.append('UserId', authData.userId);
      if (authData?.hotelId) formData.append('HotelId', authData.hotelId);

      const response = await axiosInstance.post('/EmailUpload/kyc-email', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (response.status === 200) {
        toast.success('Documents submitted for verification');
        setKycStatus('Pending');
        clearCaptureSession();
        setPhoneCaptureEnabled(false);
        setTimeout(() => navigate('/kyc-verification'), 1500);
      } else {
        throw new Error(response.data?.message || 'Submission failed');
      }
    } catch (error) {
      const data = error.response?.data;

      const errorMsg = data?.message || error.message || 'Failed to submit documents. Please try again.';
      setMessage(errorMsg);
      toast.error(errorMsg);

      if (Array.isArray(data?.warnings)) setApiWarnings(data.warnings);
      if (typeof data?.confidence === 'number') setApiConfidence(data.confidence);
    } finally {
      setIsLoading(false);
    }
  };

  // -------------------- UI STATES --------------------
  if (isCheckingStatus) {
    return (
      <div className="kyc-upload-container">
        <div className="kyc-upload-box">
          <h2>Checking Verification Status</h2>
          <p>Please wait while we check your KYC status...</p>
        </div>
      </div>
    );
  }

  if (kycStatus === 'Approved') {
    return (
      <div className="kyc-upload-container">
        <div className="kyc-upload-box">
          <h2>Verification Complete</h2>

          <div className="kyc-status-message success">
            <p>Your KYC documents have been approved.</p>
            <p>You now have full access to all platform features.</p>
          </div>

          <div className="activation-cta">
            <h3>Next Steps</h3>
            <p>You can now start your free trial or activate your account:</p>

            <div className="cta-buttons">
              <Link to="/setup-hotel" className="btn btn-primary">
                Setup Your Hotel &amp; Start Trial
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === 'Pending') {
    return (
      <div className="kyc-upload-container">
        <div className="kyc-upload-box">
          <h2>Verification In Progress</h2>

          <div className="kyc-status-message warning">
            <p>Your documents are under review (usually takes 1-3 business days).</p>

            <div className="admin-contact">
              <p>For urgent inquiries, contact our administrators:</p>
              <ul>
                {adminEmails.map((email, index) => (
                  <li key={index}>
                    <a href={`mailto:${email}?subject=KYC Status Inquiry`}>{email}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (kycStatus === 'Rejected') {
    return (
      <div className="kyc-upload-container">
        <div className="kyc-upload-box">
          <h2>Verification Rejected</h2>

          <div className="kyc-status-message error">
            <p>Your submission didn&apos;t meet our requirements. Please check:</p>
            <ul>
              <li>Documents must be clear and legible</li>
              <li>Files must show complete information</li>
              <li>Documents must not be expired</li>
            </ul>

            <div className="admin-contact">
              <p>For specific reasons, contact our administrators:</p>
              <ul>
                {adminEmails.map((email, index) => (
                  <li key={index}>
                    <a href={`mailto:${email}?subject=KYC Rejection Details`}>{email}</a>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => {
                setKycStatus(null);
                resetApiFeedback();
                setIdentityDocument(null);
                setAddressProof(null);
                clearCaptureSession();
                setPhoneCaptureEnabled(false);
              }}
              className="retry-button"
              disabled={isLoading}
              type="button"
            >
              {isLoading ? 'Preparing Form...' : 'Submit New Documents'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showUploadFeedback = apiConfidence !== null || apiWarnings.length > 0 || isTroubleMessage(message);

  // -------------------- MAIN FORM (ONE OR TWO COLUMN) --------------------
  return (
    <div className="kyc-upload-container">
      <div className="kyc-two-col">
        {/* LEFT: MAIN FORM */}
        <div className="kyc-card kyc-card-main">
          <h2>Identity Verification</h2>
          <p className="form-description">Please upload clear copies of your documents for verification.</p>

          <form onSubmit={handleSubmit} className="kyc-upload-form">
            <div className="form-group">
              <label htmlFor="identityDocument">
                Government-Issued ID: <span className="required">*</span>
              </label>
              <input
                type="file"
                id="identityDocument"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  setIdentityDocument(e.target.files?.[0] || null);
                  resetApiFeedback();
                }}
                required
                disabled={isLoading}
              />
              {identityDocument && <small className="form-text">Selected: {identityDocument.name}</small>}
              <small className="form-text">(Passport, Driver&apos;s License, or National ID)</small>
            </div>

            <div className="form-group">
              <label htmlFor="addressProof">
                Proof of Address: <span className="required">*</span>
              </label>
              <input
                type="file"
                id="addressProof"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => {
                  setAddressProof(e.target.files?.[0] || null);
                  resetApiFeedback();
                }}
                required
                disabled={isLoading}
              />
              {addressProof && <small className="form-text">Selected: {addressProof.name}</small>}
              <small className="form-text">(Utility bill, bank statement, or lease agreement)</small>
            </div>

            {isLoading && (
              <div className="upload-progress">
                <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
                  {uploadProgress}%
                </div>
              </div>
            )}

            {showUploadFeedback && (
              <div className="kyc-status-message warning" style={{ marginTop: 12 }}>
                <p>
                  <strong>Upload feedback</strong>
                </p>

                {apiConfidence !== null && (
                  <p>
                    Recognition confidence: <strong>{apiConfidence.toFixed(2)}</strong>
                    {apiConfidence < MIN_CONFIDENCE && (
                      <>
                        {' '}
                        <span style={{ fontWeight: 500 }}>(Please upload a clearer ID before submitting.)</span>
                      </>
                    )}
                  </p>
                )}

                {apiWarnings.length > 0 && (
                  <ul>
                    {apiWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}

                {isTroubleMessage(message) && <p style={{ marginTop: 10 }}>{message}</p>}
              </div>
            )}

            <div className="form-footer">
              <button
                type="submit"
                className="submit-button"
                disabled={!canSubmit}
                title={
                  apiConfidence !== null && apiConfidence < MIN_CONFIDENCE
                    ? 'Please upload a clearer ID document.'
                    : undefined
                }
              >
                {isLoading ? 'Submitting...' : 'Submit Documents'}
              </button>

              {message && !isTroubleMessage(message) && <p className="error-message">{message}</p>}
            </div>
          </form>

          {!showTroubleColumn && (
            <div className="help-text">
              <p>Need help capturing clearer photos? Phone capture will appear after a recognition issue.</p>
            </div>
          )}
        </div>

        {/* RIGHT: ONLY WHEN HAVING TROUBLE (OR ENABLED) */}
        {showTroubleColumn && (
          <aside className="kyc-card kyc-card-side">
            <div className="kyc-side-header">
              <h3>Having trouble?</h3>
              <p className="small">
                Capture with your phone for clearer photos. We will import them into this form automatically.
              </p>
            </div>

            {!phoneCaptureEnabled ? (
              <>
                <button
                  type="button"
                  className="kyc-side-button"
                  onClick={() => setPhoneCaptureEnabled(true)}
                  disabled={!token || isCreatingCapture}
                >
                  Enable Phone Capture
                </button>

                <ul className="kyc-side-bullets small">
                  <li>Use your phone camera</li>
                  <li>Auto-import into this form</li>
                  <li>No extra login needed</li>
                </ul>

                {apiConfidence !== null && apiConfidence < MIN_CONFIDENCE && (
                  <div className="kyc-side-hint">Your last upload was unclear. Phone capture is recommended.</div>
                )}

                <p className="small" style={{ marginTop: 10 }}>
                  Tip: Use bright lighting, avoid glare, and ensure the entire document is visible.
                </p>

                <div className="small" style={{ marginTop: 10 }}>
                  Contact support:{' '}
                  <a href={`mailto:${adminEmails?.[0] || 'support@hms.com'}`}>
                    {adminEmails?.[0] || 'support@hms.com'}
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="kyc-qr-wrap">
                  {captureQrDataUrl ? (
                    <img src={captureQrDataUrl} alt="KYC Capture QR" className="kyc-qr" />
                  ) : (
                    <div className="kyc-qr-placeholder">
                      {isCreatingCapture ? 'Generating QR…' : 'Preparing QR…'}
                    </div>
                  )}
                </div>

                <div className="small" style={{ marginTop: 10 }}>
                  {captureSession?.expiresAtUtc && (
                    <div>
                      <strong>Expires:</strong> {safeIsoToLocal(captureSession.expiresAtUtc)}
                    </div>
                  )}

                  {captureSession?.captureUrl && (
                    <div style={{ marginTop: 6 }}>
                      If you cannot scan, open this link on your phone:
                      <div className="kyc-capture-link">{captureSession.captureUrl}</div>
                    </div>
                  )}

                  {captureError && (
                    <div className="error-message" style={{ marginTop: 10 }}>
                      {captureError}
                    </div>
                  )}
                </div>

                <div className="kyc-side-status">
                  <div className="small" style={{ fontWeight: 700, marginBottom: 6 }}>
                    Status
                  </div>
                  <ul className="small">
                    <li>Identity: {captureStatus?.hasIdentity ? 'Uploaded' : 'Waiting'}</li>
                    <li>Address Proof: {captureStatus?.hasAddress ? 'Uploaded' : 'Waiting'}</li>
                    <li>Session: {captureStatus?.status || 'Active'}</li>
                  </ul>
                </div>

                <div className="kyc-side-actions">
                  <button
                    type="button"
                    className="kyc-side-button secondary"
                    onClick={() => createCaptureSessionInternal(true)}
                    disabled={isCreatingCapture}
                  >
                    Refresh QR
                  </button>

                  <button
                    type="button"
                    className="kyc-side-button danger"
                    onClick={() => {
                      clearCaptureSession();
                      setPhoneCaptureEnabled(false);
                    }}
                    disabled={isCreatingCapture}
                  >
                    Disable
                  </button>
                </div>

                <p className="small" style={{ marginTop: 10 }}>
                  Tip: Use bright lighting, avoid glare, and ensure the entire document is visible.
                </p>

                <div className="small" style={{ marginTop: 10 }}>
                  Contact support:{' '}
                  <a href={`mailto:${adminEmails?.[0] || 'support@hms.com'}`}>
                    {adminEmails?.[0] || 'support@hms.com'}
                  </a>
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
};

export default KYCUploadPage;
